import Chart from 'react-apexcharts'
import useTwinTranslation from '../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useState } from 'react'
import { twinFetchPostJSON } from '../../../../utils/globals/data'
import useDictMonths from '../../../../utils/hooks/useDictMonths'
import { sellInvoiceStatus, SellInvoiceStatusKeys } from '@teinor/erp/types/company/order/sell/sellInvoice'
import { SellInvoiceStatusPercentageData, SellInvoiceStatusPercentageDataKeys } from './types'
import './sellinvoicestatuspercentage.sass'

interface DSBSellInvoiceStatusPercentageProps extends DSBSellInvoiceStatusPercentageLogicProps {
    title?: string
}

const DSBSellInvoiceStatusPercentage = ({title, end_date, start_date }: DSBSellInvoiceStatusPercentageProps) => {
    const { t } = useTwinTranslation()
    const { sellInvoices } = useDSBSellInvoiceStatusPercentageLogic({end_date, start_date})
    const {arrMonthShort} = useDictMonths()
    const customlegendFormatter = (name: any) => {
        return `<div class='legend-label'>${name}</div>`
    }
    const series = [
        {
            name: t('creating', 'Borrador'),
            type: 'column',
            data: sellInvoices.creating
        },
        {
            name: t('accepted', 'Aceptados'),
            type: 'column',
            data: sellInvoices.accepted
        },
        {
            name: t('refunded', 'Devueltos'),
            type: 'column',
            data: sellInvoices.refunded
        },
    ]
    return (
        <div className='flex flex-col flex-auto h-1 gap-10 sell_invoice_status_percentage'>
            <h3>{title}</h3>
            <div className='flex-auto h-1'>
                <Chart options={{
                    chart: {
                        type: 'bar',
                        stacked: true,
                        events: {
                            mounted: (chart) => {
                                chart.windowResizeHandler()
                            }
                        },
                        toolbar: {
                            show: false
                        },
                        zoom: {
                            enabled: true
                        }
                    },
                    plotOptions: {
                        bar: {
                            horizontal: false,
                            columnWidth: '100%',
                        },
                    },
                    stroke: {
                        show: true,
                        width: 2,
                        colors: ['transparent']
                    },
                    yaxis: {
                        labels: {
                            style: {
                                colors: '#8F9BA4',
                                fontFamily: 'Roboto'
                            }
                        },
                        max: 100,
                    },
                    xaxis: {
                        labels: {
                            style: {
                                fontFamily: 'Roboto',
                                colors: '#8F9BA4',
                            }
                        },
                        categories: arrMonthShort
                    },
                    fill: {
                        opacity: 1,
                    },
                    legend: {
                        position: 'top',
                        horizontalAlign: 'left',
                        formatter: customlegendFormatter,
                        fontFamily: 'Roboto',
                    },
                    colors: ['#8F9BA4','#1DB27B', '#EB2D53'],
                    tooltip: {
                        y: {
                            formatter: function (val) {
                                return val + "%"
                            }
                        }
                    }
                }} series={series} type="bar" height={'100%'} />
            </div>
        </div>
    )
}

interface DSBSellInvoiceStatusPercentageLogicProps {
    start_date: Date
    end_date: Date
}

const useDSBSellInvoiceStatusPercentageLogic = ({end_date, start_date}: DSBSellInvoiceStatusPercentageLogicProps) => {
    const [sellInvoices, setSellInvoices] = useState<SellInvoiceStatusPercentageData>({ creating: [], accepted: [], refunded: [] })

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/order/sell/sellInvoice/getAllSellInvoicesForStats', {
            attributes: ['status', 'createdAt'],
            where: {
                'SellInvoice.createdAt': { '$betweenN': [start_date, end_date] }, is_last: true}
        })
        const totals = Array(12).fill(0)
        const mySellInvoiceStatus: SellInvoiceStatusPercentageData = { creating: Array(12).fill(0), accepted: Array(12).fill(0), refunded: Array(12).fill(0)}

        if (result) {
            for (const element of result) {
                const date = new Date(element.createdAt)
                const month = date.getMonth()
                totals[month] += 1
                let myStatusInversed = sellInvoiceStatus?.[element.status as SellInvoiceStatusKeys] as SellInvoiceStatusPercentageDataKeys
                mySellInvoiceStatus[myStatusInversed][month] += 1
            }
            for (let key in totals) {
                mySellInvoiceStatus.accepted[key] = mySellInvoiceStatus.accepted[key] ? parseInt(String((mySellInvoiceStatus.accepted[key] / totals[key]) * 100)) : 0
                mySellInvoiceStatus.refunded[key] = mySellInvoiceStatus.refunded[key] ? parseInt(String((mySellInvoiceStatus.refunded[key] / totals[key]) * 100)) : 0
            }
            setSellInvoices(mySellInvoiceStatus)
        }
    }, [setSellInvoices, end_date, start_date])

    useEffect(() => {
        getData()
    }, [getData])

    return {sellInvoices}
}


export default DSBSellInvoiceStatusPercentage